/* ==========================================================================
   OBLIVION — delivery estimate widget
   Pincode → expected delivery date from the Deodap shipping panel:

     GET {apiBase}/{pincode}?mode={average|p75|p90}
     → { success, pincode, mode, edd_days, edd_date,
         estimates: { average:{days,date}, p75:{days,date}, p90:{days,date} },
         basis: pincode|district|region|national, confidence: high|medium|low }

   Fallback when the API is unreachable: assets/oblivion-edd-table.json, a
   table of average delivery days built from the same panel's delivered
   orders (exact pincode → 3-digit district → 2-digit region → national).
   The API is tried first on every check; the table only answers when the
   API fails, and table answers are cached for one hour so the API takes
   over again quickly once it is back.

   One script serves all three product-page families:
     • Oblivion PDP   sections/oblivion-product.liquid renders the snippet
     • Horizon PDP    blocks/oblivion-edd.liquid renders the snippet
     • GemPages PDP   no slot — the widget is cloned from #ob-edd-template
                      and inserted under the buy buttons of the first
                      product block (autoMount)

   Config: the #ob-edd-config JSON emitted by snippets/oblivion-edd-head.liquid.

   Behaviour notes
   - The date shown is computed on the shopper's device (today + days), not
     taken from the API's edd_date, so a server clock in another timezone
     can never show a date that is a day off.
   - A pincode is remembered (localStorage ob_pin) only after it produced an
     estimate, and re-checked quietly on the next product page: a failing
     automatic check resets to the idle state instead of showing an error.
   - An API failure marks the API as down for 30 minutes (sessionStorage);
     checks in that window go straight to the table.
   - Successful estimates are cached in sessionStorage per mode+pincode
     (6 h for API answers, 1 h for table answers).
   - No dependencies. ES5 syntax on purpose (older Android WebViews).
   ========================================================================== */
(function () {
  'use strict';
  if (window.OBEDD && window.OBEDD.version) return;

  var cfg = readConfig();
  if (!cfg) return;

  var DEFAULT_API = 'https://ship.deodap.in/api/edd';
  var LS_PIN = 'ob_pin';
  var SS_DOWN = 'ob_edd_down';
  var SS_CACHE = 'ob_edd:';
  var CACHE_TTL_API = 6 * 3600e3;
  var CACHE_TTL_TABLE = 3600e3;
  var DOWN_TTL = 30 * 60e3;
  var MAX_DAYS = 60;
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var MODES = { average: 1, p75: 1, p90: 1, range: 1 };
  var mode = MODES[cfg.mode] ? cfg.mode : 'p90';
  var extraDays = parseInt(cfg.extraDays, 10) || 0;
  var T = cfg.texts || {};
  var inflight = Object.create(null);
  var instances = [];
  var tablePromise = null;

  /* ---------------- helpers ---------------- */
  function readConfig() {
    var el = document.getElementById('ob-edd-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return null; }
  }
  function text(key, fallback) {
    return typeof T[key] === 'string' && T[key] !== '' ? T[key] : fallback;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* escape the template, then drop each {placeholder} in as <b>value</b> */
  function fillHtml(tpl, vars) {
    return esc(tpl).replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? '<b>' + esc(vars[k]) + '</b>' : m;
    });
  }
  function fillText(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m;
    });
  }
  function lsGet(k) { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* ignore */ } }
  function ssDel(k) { try { sessionStorage.removeItem(k); } catch (e) { /* ignore */ } }
  function isPin(p) { return /^[1-9][0-9]{5}$/.test(p); }
  function isNum(v) { return typeof v === 'number' && isFinite(v); }
  function fmtDate(d) { return DAYS[d.getDay()] + ', ' + d.getDate() + ' ' + MONTHS[d.getMonth()]; }
  function dateIn(days) {
    var d = new Date();
    d.setHours(12, 0, 0, 0);          /* noon: immune to DST edge cases */
    d.setDate(d.getDate() + days);
    return d;
  }
  function emit(el, name, detail) {
    try { el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail })); } catch (e) { /* old WebView */ }
  }
  function canFetch() { return typeof Promise === 'function' && typeof fetch === 'function'; }

  /* ---------------- API ---------------- */
  function apiBase() {
    var b = String(cfg.apiBase || '').trim().replace(/\/+$/, '');
    return /^https:\/\/[^\s?#]+$/i.test(b) ? b : DEFAULT_API;
  }
  function apiUrl(pin) {
    var m = mode === 'range' ? 'p90' : mode;
    return apiBase() + '/' + encodeURIComponent(pin) + '?mode=' + encodeURIComponent(m);
  }
  function apiDown() {
    var t = parseInt(ssGet(SS_DOWN), 10);
    return !!t && (Date.now() - t) < DOWN_TTL;
  }
  function markDown() { ssSet(SS_DOWN, String(Date.now())); }

  /* raw day counts for the configured mode (before the merchant's buffer) */
  function normalise(j, pin) {
    if (j.pincode && String(j.pincode) !== pin) throw new Error('pincode_mismatch');
    var est = j.estimates || {};
    function days(k) {
      var v = est[k] && est[k].days;
      return isNum(v) ? v : null;
    }
    var fallback = isNum(j.edd_days) ? j.edd_days : null;
    var lo, hi;
    if (mode === 'range') {
      lo = days('average'); hi = days('p90');
      if (lo == null) lo = fallback;
      if (hi == null) hi = fallback;
    } else {
      lo = hi = days(mode);
      if (lo == null) lo = hi = fallback;
    }
    if (lo == null || hi == null || lo < 0 || hi < lo || hi > MAX_DAYS) throw new Error('nodays');
    return { pin: pin, lo: lo, hi: hi, confidence: String(j.confidence || ''), basis: String(j.basis || ''), src: 'api' };
  }

  function apiEstimate(pin) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = null;
    var p = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (ctrl) ctrl.abort();
        reject(new Error('timeout'));
      }, cfg.timeoutMs || 7000);
      fetch(apiUrl(pin), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: ctrl ? ctrl.signal : undefined
      })
        .then(function (r) {
          if (!r.ok) throw new Error('http_' + r.status);
          return r.json();
        })
        .then(function (j) {
          if (!j || j.success === false) throw new Error('api');
          resolve(normalise(j, pin));
        })
        .catch(function (err) { reject(err && err.message ? err : new Error('network')); });
    });
    p.then(function () { clearTimeout(timer); }, function () { clearTimeout(timer); });
    return p;
  }

  /* ---------------- built-in table (fallback) ---------------- */
  function loadTable() {
    if (tablePromise) return tablePromise;
    if (!cfg.tableUrl) return Promise.reject(new Error('notable'));
    tablePromise = fetch(cfg.tableUrl, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('table_http_' + r.status); return r.json(); })
      .then(function (t) { if (!t || !t.p3) throw new Error('table_bad'); return t; });
    tablePromise.then(null, function () { tablePromise = null; });   /* allow a later retry */
    return tablePromise;
  }
  /* exact pincode → district → region → national; the table holds average
     days, so the percentile modes are derived with the same uplift the
     panel's own histogram showed (p90 ≈ 1.25 × average + 0.5) */
  function tableLookup(t, pin) {
    var v = null, basis = '', conf = '', range = null;
    if (t.p6r && t.p6r[pin]) {
      var pair = t.p6r[pin];
      if (Object.prototype.toString.call(pair) === '[object Array]' && pair.length >= 2) {
        var rlo = Number(pair[0]);
        var rhi = Number(pair[1]);
        if (isFinite(rlo) && isFinite(rhi) && rlo >= 0 && rhi >= rlo && rhi <= MAX_DAYS) {
          range = { lo: rlo, hi: rhi };
          basis = 'pincode';
          conf = 'high';
        }
      }
    }
    if (!range && t.p6 && isNum(t.p6[pin])) { v = t.p6[pin]; basis = 'pincode'; conf = 'high'; }
    else if (!range && t.p3 && isNum(t.p3[pin.slice(0, 3)])) { v = t.p3[pin.slice(0, 3)]; basis = 'district'; conf = 'medium'; }
    else if (!range && t.p2 && isNum(t.p2[pin.slice(0, 2)])) { v = t.p2[pin.slice(0, 2)]; basis = 'region'; conf = 'low'; }
    else if (!range && isNum(t.nat)) { v = t.nat; basis = 'national'; conf = 'low'; }
    else if (!range) return null;
    if (range) {
      var rangeLo = Math.ceil(range.lo - 1e-9);
      var rangeHi = Math.ceil(range.hi - 1e-9);
      if (mode === 'range') return { pin: pin, lo: rangeLo, hi: rangeHi, confidence: conf, basis: basis, src: 'table' };
      var rangeDays = mode === 'average' ? Math.ceil((range.lo + range.hi) / 2 - 1e-9) : rangeHi;
      return { pin: pin, lo: rangeDays, hi: rangeDays, confidence: conf, basis: basis, src: 'table' };
    }
    var avg = Math.ceil(v - 1e-9);
    var p75 = Math.ceil(v * 1.1 + 0.3);
    var p90 = Math.ceil(v * 1.25 + 0.5);
    var lo, hi;
    if (mode === 'range') { lo = avg; hi = p90; }
    else { lo = hi = mode === 'average' ? avg : mode === 'p75' ? p75 : p90; }
    return { pin: pin, lo: lo, hi: hi, confidence: conf, basis: basis, src: 'table' };
  }
  function tableEstimate(pin) {
    return loadTable().then(function (t) {
      var raw = tableLookup(t, pin);
      if (!raw) throw new Error('table_miss');
      return raw;
    });
  }

  /* ---------------- estimate (API → table) ---------------- */
  /* apply the buffer and sane bounds at display time, so a settings change is never cached */
  function finish(raw, cached) {
    var lo = Math.min(MAX_DAYS, Math.max(0, Math.round(raw.lo) + extraDays));
    var hi = Math.min(MAX_DAYS, Math.max(lo, Math.round(raw.hi) + extraDays));
    return { pin: raw.pin, lo: lo, hi: hi, confidence: raw.confidence, basis: raw.basis, src: raw.src || 'api', cached: !!cached };
  }
  function cacheKey(pin) { return SS_CACHE + mode + ':' + pin; }
  function readCache(pin) {
    try {
      var o = JSON.parse(ssGet(cacheKey(pin)) || 'null');
      if (!o || !isNum(o.ts) || !isNum(o.lo) || !isNum(o.hi)) return null;
      var ttl = o.src === 'table' ? CACHE_TTL_TABLE : CACHE_TTL_API;
      if (Date.now() - o.ts > ttl) return null;
      return { pin: pin, lo: o.lo, hi: o.hi, confidence: String(o.confidence || ''), basis: String(o.basis || ''), src: o.src === 'table' ? 'table' : 'api' };
    } catch (e) { return null; }
  }
  function writeCache(raw) {
    ssSet(cacheKey(raw.pin), JSON.stringify({ lo: raw.lo, hi: raw.hi, confidence: raw.confidence, basis: raw.basis, src: raw.src, ts: Date.now() }));
  }

  function fetchEstimate(pin) {
    if (!canFetch()) { throw new Error('nofetch'); }
    if (!isPin(pin)) return Promise.reject(new Error('invalid'));
    var hit = readCache(pin);
    if (hit) return Promise.resolve(finish(hit, true));
    if (inflight[pin]) return inflight[pin];

    var first = apiDown() ? Promise.reject(new Error('api_down')) : apiEstimate(pin);
    var p = first
      .then(null, function (err) {
        var code = (err && err.message) || 'network';
        if (/^(http_404|http_5\d\d|timeout|network|api|nodays)$/.test(code)) markDown();
        /* the table answers whenever it can; otherwise the API error stands */
        return tableEstimate(pin).then(null, function () { throw err; });
      })
      .then(function (raw) {
        if (raw.src === 'api') ssDel(SS_DOWN);
        writeCache(raw);
        return finish(raw, false);
      });
    p.then(function () { delete inflight[pin]; }, function () { delete inflight[pin]; });
    inflight[pin] = p;
    return p;
  }

  /* ---------------- widget ---------------- */
  function Widget(root) {
    this.root = root;
    this.input = root.querySelector('[data-ob-edd-input]');
    this.btn = root.querySelector('[data-ob-edd-btn]');
    this.msg = root.querySelector('[data-ob-edd-msg]');
    this.title = root.querySelector('[data-ob-edd-title]');
    this.change = root.querySelector('[data-ob-edd-change]');
    this.heading = (this.title && this.title.getAttribute('data-ob-edd-heading')) || text('heading', 'Check delivery date');
    this.token = 0;
    this.busy = false;
    if (!this.input || !this.btn || !this.msg) return;
    root.__obEdd = this;
    root.classList.add('ob-edd--ready');

    var self = this;
    this.btn.addEventListener('click', function () { self.check(); });
    this.input.addEventListener('keydown', function (e) {
      /* Enter must never reach a surrounding add-to-cart form (GemPages) */
      if (e.key === 'Enter' || e.keyCode === 13) { e.preventDefault(); e.stopPropagation(); self.check(); }
    });
    this.input.addEventListener('input', function () {
      var v = self.input.value.replace(/\D/g, '').slice(0, 6);
      if (v !== self.input.value) self.input.value = v;
      self.input.removeAttribute('aria-invalid');
      /* the value changed: whatever is in flight is for a different pincode */
      if (self.busy) { ++self.token; self.idle(); }
      if (v.length === 6 && isPin(v)) self.check();   /* auto-check on the 6th digit */
    });
    if (this.change) this.change.addEventListener('click', function () { self.edit(); });

    var saved = lsGet(LS_PIN);
    if (isPin(saved)) {
      this.input.value = saved;
      this.check(undefined, true);
    }
  }

  Widget.prototype.setState = function (state) {
    var c = this.root.classList;
    c.remove('ob-edd--ok'); c.remove('ob-edd--err'); c.remove('ob-edd--soft'); c.remove('ob-edd--busy');
    if (state) c.add('ob-edd--' + state);
  };

  /* the generic "Usually delivered in 3–6 days" line next to the widget is
     redundant once a real date is showing */
  Widget.prototype.generic = function (show) {
    var host = this.root.parentNode;
    if (!host || !host.querySelectorAll) return;
    var rows = host.querySelectorAll('[data-ob-edd-generic]');
    for (var i = 0; i < rows.length; i++) rows[i].hidden = !show;
  };

  Widget.prototype.idle = function () {
    this.busy = false;
    this.setState(null);
    this.msg.textContent = '';
    if (this.title) this.title.textContent = this.heading;
    if (this.change) this.change.hidden = true;
    this.generic(true);
  };

  /* auto = the quiet page-load re-check of a remembered pincode */
  Widget.prototype.check = function (pinArg, auto) {
    if (typeof pinArg === 'string') this.input.value = pinArg;
    var pin = (this.input.value || '').trim();
    var self = this;
    ++this.token;                                   /* anything in flight is now stale */
    if (!isPin(pin)) {
      if (auto) return;
      this.busy = false;
      this.setState('err');
      this.input.setAttribute('aria-invalid', 'true');
      this.msg.textContent = text('invalid', 'Please enter a valid 6-digit pincode.');
      this.input.focus();
      return;
    }
    if (!canFetch()) { if (!auto) this.fail(new Error('nofetch'), false); return; }
    var token = this.token;
    this.busy = true;
    this.setState('busy');
    this.msg.textContent = auto ? '' : fillText(text('checking', 'Checking delivery for {pin}…'), { pin: pin });
    fetchEstimate(pin).then(function (est) {
      if (token !== self.token) return;             /* superseded */
      self.busy = false;
      self.show(est, auto);
    }, function (err) {
      if (token !== self.token) return;
      self.busy = false;
      self.fail(err, auto);
    });
  };

  Widget.prototype.show = function (est, auto) {
    if (est.pin !== (this.input.value || '').trim()) return;   /* typed over meanwhile */
    var html;
    if (est.lo === est.hi) {
      html = fillHtml(text('result', 'Get it by {date}'), { date: fmtDate(dateIn(est.hi)), days: est.hi, pin: est.pin });
    } else {
      html = fillHtml(text('rangeResult', 'Expected between {from} and {to}'), {
        from: fmtDate(dateIn(est.lo)), to: fmtDate(dateIn(est.hi)), fromDays: est.lo, toDays: est.hi, pin: est.pin
      });
    }
    if (cfg.showConfidence !== false && est.confidence && est.confidence !== 'high') {
      html += '<span class="ob-edd__sub">' + esc(text('lowConfidence', 'Estimate based on deliveries to nearby areas')) + '</span>';
    }
    var ae = document.activeElement;
    var focusInside = !auto && ae && ae !== document.body && this.root.contains(ae);

    this.msg.innerHTML = html;
    if (this.title) this.title.innerHTML = fillHtml(text('deliverTo', 'Delivering to {pin}'), { pin: est.pin });
    if (this.change) this.change.hidden = false;
    this.input.removeAttribute('aria-invalid');
    this.setState('ok');
    this.generic(false);
    lsSet(LS_PIN, est.pin);
    /* the input row is hidden now — keep keyboard focus inside the widget */
    if (focusInside && this.change) {
      try { this.change.focus({ preventScroll: true }); } catch (e) { try { this.change.focus(); } catch (e2) { /* ignore */ } }
    }
    try {
      (window.dataLayer = window.dataLayer || []).push({
        event: 'ob_edd_result', edd_pin: est.pin, edd_days: est.hi, edd_days_lo: est.lo,
        edd_basis: est.basis, edd_confidence: est.confidence, edd_mode: mode, edd_source: est.src,
        edd_auto: !!auto, edd_cached: !!est.cached
      });
      if (typeof window.clarity === 'function') window.clarity('set', 'edd_basis', est.basis || 'unknown');
    } catch (e) { /* analytics is best-effort */ }
    emit(this.root, 'ob:edd', est);
  };

  Widget.prototype.fail = function (err, auto) {
    var code = (err && err.message) || 'error';
    if (/^(http_404|http_5\d\d|timeout|network|nofetch)$/.test(code)) markDown();
    if (auto) { this.idle(); return; }                /* quiet: never an unprompted error */
    this.setState('soft');
    this.msg.textContent = text('error', 'Could not check delivery for this pincode right now. Please try again in a moment.');
    if (this.title) this.title.textContent = this.heading;
    if (this.change) this.change.hidden = true;
    this.generic(true);
    try {
      (window.dataLayer = window.dataLayer || []).push({ event: 'ob_edd_error', edd_pin: this.input.value, edd_error: code });
    } catch (e) { /* ignore */ }
    emit(this.root, 'ob:edd:error', { pin: this.input.value, error: code });
  };

  Widget.prototype.edit = function () {
    ++this.token;                       /* drop any in-flight result */
    this.idle();
    this.input.focus();
    try { this.input.select(); } catch (e) { /* ignore */ }
  };

  /* ---------------- availability ---------------- */
  /* no dated promise on a variant that cannot be bought */
  function setAvailable(avail) {
    for (var i = 0; i < instances.length; i++) instances[i].root.hidden = (avail === false);
  }

  /* ---------------- mounting ---------------- */
  function excluded(el) {
    /* Horizon's quick-add modal clones the whole product-details group;
       the copy must stay inert (and is hidden by CSS) */
    return !!(el.closest && el.closest('#quick-add-modal-content, quick-add-dialog, template'));
  }
  function hydrateAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-ob-edd]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].__obEdd || excluded(nodes[i])) continue;
      var w = new Widget(nodes[i]);
      if (nodes[i].__obEdd) instances.push(w);
    }
  }
  function prune() {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (!document.body || !document.body.contains(instances[i].root)) instances.splice(i, 1);
    }
  }
  function hasWidget() {
    var nodes = document.querySelectorAll('body [data-ob-edd]');
    for (var i = 0; i < nodes.length; i++) if (!excluded(nodes[i])) return true;
    return false;
  }

  /* GemPages: the first product block that is not the sticky bar and has an
     add-to-cart form; the widget goes right after its last buy button.
     "Buy now" is wrapped in <gp-animation><div><div>…</div></div></gp-animation>
     that GemPages animates, so the anchor is that wrapper (never inside it),
     unless the animation wraps the whole product block. */
  function gemPagesAnchor() {
    var products = document.querySelectorAll('gp-product');
    for (var i = 0; i < products.length; i++) {
      var gp = products[i];
      if (gp.closest('gp-sticky')) continue;
      if (!gp.querySelector('form[action*="/cart/add"]')) continue;
      var btns = gp.querySelectorAll('gp-product-button, gp-product-dynamic');
      if (!btns.length) continue;
      var last = btns[btns.length - 1];
      var anchor = last;
      var anim = last.closest('gp-animation');
      if (anim && gp.contains(anim)
        && !anim.querySelector('gp-product-images, gp-product-price, gp-product-quantity, gp-product-variant, gp-product-title')) {
        anchor = anim;
      }
      return anchor;
    }
    return null;
  }

  function mountGemPages() {
    if (!cfg.autoMount) return true;
    if (hasWidget()) return true;
    var tpl = document.getElementById('ob-edd-template');
    if (!tpl || !tpl.content) return true;
    var anchor = null;
    try { anchor = gemPagesAnchor(); } catch (e) { return true; }
    if (!anchor || !anchor.parentNode) return false;
    anchor.parentNode.insertBefore(document.importNode(tpl.content, true), anchor.nextSibling);
    hydrateAll();
    return true;
  }

  var watcher = null;
  function watchGemPages() {
    if (mountGemPages()) return;
    if (watcher) return;
    if (typeof MutationObserver === 'function' && typeof requestAnimationFrame === 'function') {
      var pending = false;
      var mo = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          if (mountGemPages()) stop();
        });
      });
      var stopTimer = setTimeout(function () { stop(); }, 60000);
      var stop = function () { mo.disconnect(); clearTimeout(stopTimer); watcher = null; };
      watcher = stop;
      mo.observe(document.body, { childList: true, subtree: true });
    } else {
      var tries = 0;
      var iv = setInterval(function () {
        if (mountGemPages() || ++tries >= 40) { clearInterval(iv); watcher = null; }
      }, 250);
      watcher = function () { clearInterval(iv); watcher = null; };
    }
  }

  function boot() {
    hydrateAll();
    watchGemPages();
    /* theme editor re-renders sections (the GemPages copy must be re-inserted) */
    document.addEventListener('shopify:section:load', function (e) { prune(); hydrateAll(e.target); watchGemPages(); });
    document.addEventListener('shopify:section:unload', prune);
    /* Horizon variant picker */
    document.addEventListener('variant:update', function (e) {
      var d = e && e.detail;
      var v = d && (d.resource || d.variant);
      if (v && typeof v.available === 'boolean') setAvailable(v.available);
    });
  }

  window.OBEDD = {
    version: '1.3.0',
    instances: instances,
    /* OBEDD.check('360003') — or no argument to check what is typed */
    check: function (pin) {
      prune();
      var w = instances[0];
      if (!w) return;
      w.check(pin == null ? undefined : String(pin).trim());
    },
    setAvailable: setAvailable,
    estimate: fetchEstimate,
    hydrate: hydrateAll
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
